import {
  initializeApp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-app.js';
import {
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

function esc(str) {
  const d = document.createElement('div');
  d.textContent = str == null ? '' : String(str);
  return d.innerHTML;
}

function timeAgo(date) {
  if (!date) return '';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  const steps = [['y',31536000],['mo',2592000],['d',86400],['h',3600],['m',60]];
  for (const [label, s] of steps) {
    const v = Math.floor(sec / s);
    if (v >= 1) return `${v}${label} ago`;
  }
  return 'just now';
}

function initials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length-1][0] : '')).toUpperCase() || 'U';
}

function avatarColorIndex(name) {
  const s = name || 'U';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
  return (Math.abs(h) % 6) + 1;
}

function makeAvatar(name, size = 40) {
  const el = document.createElement('span');
  el.className = 'fp-avatar';
  el.style.cssText = `width:${size}px;height:${size}px;font-size:${Math.round(size*0.38)}px`;
  el.dataset.c = String(avatarColorIndex(name));
  el.textContent = initials(name);
  el.setAttribute('aria-hidden', 'true');
  return el;
}

function setNum(el, n) { if (el) el.textContent = n.toLocaleString(); }

function emptyState(msg, ctaHtml) {
  const li = document.createElement('li');
  li.className = 'fp-empty';
  li.innerHTML = `<span>${esc(msg)}</span>${ctaHtml || ''}`;
  return li;
}

function firestoreErrorMessage(err, fallback) {
  const code = err && err.code;
  if (code === 'permission-denied') return "Couldn't save — check Firestore security rules";
  if (code === 'not-found') return 'This post no longer exists';
  if (code === 'unavailable') return 'Connection issue, please try again';
  return fallback;
}

/* Try both number and string form of customerId */
function getIdVariants(customerId) {
  const num = Number(customerId);
  if (!isNaN(num) && num > 0) {
    return [num, String(customerId)];
  }
  return [String(customerId)];
}

async function fetchMyQuestions(db, collectionName, customerId) {
  const ids = getIdVariants(customerId);
  const seen = new Set();
  const results = [];

  for (const id of ids) {
    try {
      const snap = await getDocs(
        query(
          collection(db, collectionName),
          where('authorId', '==', id),
          orderBy('createdAt', 'desc')
        )
      );
      snap.docs.forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          results.push({ id: d.id, ...d.data() });
        }
      });
    } catch (err) {
      if (err.code === 'failed-precondition') {
        try {
          const snap2 = await getDocs(
            query(
              collection(db, collectionName),
              where('authorId', '==', id)
            )
          );
          snap2.docs.forEach(d => {
            if (!seen.has(d.id)) {
              seen.add(d.id);
              results.push({ id: d.id, ...d.data() });
            }
          });
        } catch (err2) {
          throw err2;
        }
      } else {
        throw err;
      }
    }
  }

  results.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });

  return results;
}

async function fetchMyReplies(db, collectionName, customerId, myQuestions) {
  const ids = getIdVariants(customerId);

  for (const id of ids) {
    try {
      const snap = await getDocs(
        query(
          collectionGroup(db, 'replies'),
          where('authorId', '==', id),
          orderBy('createdAt', 'desc')
        )
      );
      const titleCache = {};
      myQuestions.forEach(q => { titleCache[q.id] = q.title; });

      const results = [];
      await Promise.all(snap.docs.map(async d => {
        const qId = d.ref.parent.parent.id;
        if (!titleCache[qId]) {
          try {
            const qDoc = await getDoc(doc(db, collectionName, qId));
            titleCache[qId] = qDoc.exists() ? qDoc.data().title : '(deleted question)';
          } catch { titleCache[qId] = '(unknown question)'; }
        }
        results.push({ id: d.id, questionId: qId, questionTitle: titleCache[qId], ...d.data() });
      }));

      if (results.length > 0) return results;
    } catch (err) {
      // fall through to manual scan below
    }
  }

  // Fallback: scan replies under user's own questions
  const results = [];
  for (const q of myQuestions) {
    for (const id of ids) {
      try {
        const snap = await getDocs(
          query(collection(db, collectionName, q.id, 'replies'), where('authorId', '==', id))
        );
        snap.docs.forEach(d => {
          results.push({ id: d.id, questionId: q.id, questionTitle: q.title, ...d.data() });
        });
      } catch {}
    }
  }
  return results;
}

document.querySelectorAll('[id^="fp-config-"]').forEach(async (configEl) => {
  let cfg;
  try { cfg = JSON.parse(configEl.textContent); } catch { return; }

  const { sectionId, firebaseConfig, customer, collectionName, forumPageUrl } = cfg;
  if (!customer.loggedIn) return;

  const $ = (id) => document.getElementById(id);

  const bigAvatarEl = $(`fp-big-avatar-${sectionId}`);
  if (bigAvatarEl) bigAvatarEl.appendChild(makeAvatar(customer.name, 56));

  const statQ = $(`fp-stat-questions-${sectionId}`);
  const statA = $(`fp-stat-answers-${sectionId}`);
  const statL = $(`fp-stat-likes-${sectionId}`);
  const statD = $(`fp-stat-dislikes-${sectionId}`);
  const statV = $(`fp-stat-views-${sectionId}`);
  const qList  = $(`fp-questions-list-${sectionId}`);
  const aList  = $(`fp-answers-list-${sectionId}`);
  const qCount = $(`fp-q-count-${sectionId}`);
  const aCount = $(`fp-a-count-${sectionId}`);
  const qPanel = $(`fp-q-panel-${sectionId}`);
  const aPanel = $(`fp-a-panel-${sectionId}`);
  const qSort  = $(`fp-q-sort-${sectionId}`);
  const aSort  = $(`fp-a-sort-${sectionId}`);
  const errorBanner = $(`fp-error-${sectionId}`);
  const retryBtn    = $(`fp-retry-${sectionId}`);

  if (!firebaseConfig?.apiKey || !firebaseConfig?.projectId) {
    [qList, aList].forEach(el => {
      if (el) el.innerHTML = '<li class="fp-empty">Forum not configured. Add Firebase keys in section settings.</li>';
    });
    return;
  }

  const app = initializeApp(firebaseConfig, `fp-${sectionId}`);
  const db  = getFirestore(app);

  let myQuestions = [];
  let myReplies   = [];

  function showError(show) {
    if (errorBanner) errorBanner.hidden = !show;
  }

  function askUrl(questionId) {
    return `${forumPageUrl}#question-${encodeURIComponent(questionId)}`;
  }

  function sortQuestions(list, mode) {
    const copy = list.slice();
    if (mode === 'liked') {
      copy.sort((a, b) => (((b.likes||[]).length - (b.dislikes||[]).length)) - (((a.likes||[]).length - (a.dislikes||[]).length)));
    } else if (mode === 'views') {
      copy.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
    } else {
      copy.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
    }
    return copy;
  }

  function sortReplies(list, mode) {
    const copy = list.slice();
    if (mode === 'liked') {
      copy.sort((a, b) => (((b.likes||[]).length - (b.dislikes||[]).length)) - (((a.likes||[]).length - (a.dislikes||[]).length)));
    } else {
      copy.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
    }
    return copy;
  }

  /* ── Edit / delete: question ── */
  async function deleteQuestion(id) {
    if (!confirm('Delete this question? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, collectionName, id));
      myQuestions = myQuestions.filter(q => q.id !== id);
      renderQuestions();
      recomputeStats();
    } catch (err) {
      alert(firestoreErrorMessage(err, 'Could not delete. Please try again.'));
    }
  }

  async function saveQuestionEdit(id, newTitle) {
    const trimmed = newTitle.trim();
    if (!trimmed) return false;
    try {
      await updateDoc(doc(db, collectionName, id), { title: trimmed, updatedAt: serverTimestamp() });
      const q = myQuestions.find(x => x.id === id);
      if (q) q.title = trimmed;
      return true;
    } catch (err) {
      alert(firestoreErrorMessage(err, 'Could not save. Please try again.'));
      return false;
    }
  }

  async function deleteReply(questionId, replyId) {
    if (!confirm('Delete this answer? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, collectionName, questionId, 'replies', replyId));
      myReplies = myReplies.filter(r => !(r.id === replyId && r.questionId === questionId));
      renderReplies();
      recomputeStats();
    } catch (err) {
      alert(firestoreErrorMessage(err, 'Could not delete. Please try again.'));
    }
  }

  async function saveReplyEdit(questionId, replyId, newText) {
    const trimmed = newText.trim();
    if (!trimmed) return false;
    try {
      await updateDoc(doc(db, collectionName, questionId, 'replies', replyId), { text: trimmed, updatedAt: serverTimestamp() });
      const r = myReplies.find(x => x.id === replyId && x.questionId === questionId);
      if (r) r.text = trimmed;
      return true;
    } catch (err) {
      alert(firestoreErrorMessage(err, 'Could not save. Please try again.'));
      return false;
    }
  }

  function recomputeStats() {
    let totalQLikes = 0, totalQDislikes = 0, totalViews = 0;
    myQuestions.forEach(q => {
      totalQLikes    += (q.likes    || []).length;
      totalQDislikes += (q.dislikes || []).length;
      totalViews     += (q.viewCount || 0);
    });
    let totalALikes = 0, totalADislikes = 0;
    myReplies.forEach(r => {
      totalALikes    += (r.likes    || []).length;
      totalADislikes += (r.dislikes || []).length;
    });

    setNum(statQ, myQuestions.length);
    setNum(statA, myReplies.length);
    setNum(statL, totalQLikes + totalALikes);
    setNum(statD, totalQDislikes + totalADislikes);
    if (totalViews > 0) {
      setNum(statV, totalViews);
    } else if (statV) {
      statV.textContent = '—';
      statV.closest('.fp-stat-card')?.classList.add('fp-stat-card--muted');
    }
    setNum(qCount, myQuestions.length);
    setNum(aCount, myReplies.length);
  }

  /* ── Render questions ── */
  function renderQuestions() {
    if (!qList) return;
    qList.innerHTML = '';
    if (myQuestions.length === 0) {
      qList.appendChild(emptyState(
        "You haven't asked any questions yet.",
        `<a href="${esc(forumPageUrl)}" class="fp-empty-cta">Ask one now →</a>`
      ));
      return;
    }
    const mode = qSort ? qSort.value : 'recent';
    sortQuestions(myQuestions, mode).forEach(q => {
      const date     = q.createdAt?.toDate?.() ?? null;
      const likes    = (q.likes    || []).length;
      const dislikes = (q.dislikes || []).length;
      const views    = q.viewCount || null;
      const li = document.createElement('li');
      li.className = 'fp-item';
      li.setAttribute('role', 'listitem');
      li.innerHTML = `
        <div class="fp-item-main">
          <a class="fp-item-title" href="${esc(askUrl(q.id))}">${esc(q.title)}</a>
          <div class="fp-item-meta">
            ${q.categoryLabel ? `<span class="fp-item-cat">${esc(q.categoryIcon||'')} ${esc(q.categoryLabel)}</span>` : ''}
            ${date ? `<span class="fp-item-time">${timeAgo(date)}</span>` : ''}
          </div>
          <div class="fp-item-edit" hidden>
            <input type="text" class="fp-edit-input" value="${esc(q.title)}" maxlength="200" />
            <div class="fp-edit-actions">
              <button type="button" class="fp-btn-sm fp-btn-sm--primary" data-act="save">Save</button>
              <button type="button" class="fp-btn-sm" data-act="cancel">Cancel</button>
            </div>
          </div>
        </div>
        <div class="fp-item-stats">
          <span class="fp-item-badge fp-item-badge--like" title="Likes">👍 ${likes}</span>
          <span class="fp-item-badge fp-item-badge--dislike" title="Dislikes">👎 ${dislikes}</span>
          ${views !== null ? `<span class="fp-item-badge fp-item-badge--view" title="Views">👁️ ${views}</span>` : ''}
          <div class="fp-item-actions">
            <button type="button" class="fp-icon-btn" data-act="edit" aria-label="Edit">✏️</button>
            <button type="button" class="fp-icon-btn fp-icon-btn--danger" data-act="delete" aria-label="Delete">🗑️</button>
          </div>
        </div>`;

      const editWrap  = li.querySelector('.fp-item-edit');
      const titleLink = li.querySelector('.fp-item-title');
      const metaEl    = li.querySelector('.fp-item-meta');
      const input     = li.querySelector('.fp-edit-input');

      li.querySelector('[data-act="edit"]').addEventListener('click', () => {
        editWrap.hidden = false;
        titleLink.hidden = true;
        metaEl.hidden = true;
        input.focus();
        input.select();
      });
      li.querySelector('[data-act="cancel"]').addEventListener('click', () => {
        editWrap.hidden = true;
        titleLink.hidden = false;
        metaEl.hidden = false;
        input.value = q.title;
      });
      li.querySelector('[data-act="save"]').addEventListener('click', async () => {
        const ok = await saveQuestionEdit(q.id, input.value);
        if (ok) {
          titleLink.textContent = input.value.trim();
          editWrap.hidden = true;
          titleLink.hidden = false;
          metaEl.hidden = false;
        }
      });
      li.querySelector('[data-act="delete"]').addEventListener('click', () => deleteQuestion(q.id));

      qList.appendChild(li);
    });
  }

  /* ── Render answers ── */
  function renderReplies() {
    if (!aList) return;
    aList.innerHTML = '';
    if (myReplies.length === 0) {
      aList.appendChild(emptyState(
        "You haven't answered any questions yet.",
        `<a href="${esc(forumPageUrl)}" class="fp-empty-cta">Browse questions →</a>`
      ));
      return;
    }
    const mode = aSort ? aSort.value : 'recent';
    sortReplies(myReplies, mode).forEach(r => {
      const date     = r.createdAt?.toDate?.() ?? null;
      const likes    = (r.likes    || []).length;
      const dislikes = (r.dislikes || []).length;
      const preview  = (r.text||'').slice(0,160) + ((r.text?.length||0) > 160 ? '…' : '');
      const li = document.createElement('li');
      li.className = 'fp-item';
      li.setAttribute('role', 'listitem');
      li.innerHTML = `
        <div class="fp-item-main">
          <div class="fp-item-context">Answered: <a class="fp-item-title fp-item-title--inline" href="${esc(askUrl(r.questionId))}">${esc(r.questionTitle||'View question')}</a></div>
          <p class="fp-item-preview">${esc(preview)}</p>
          <div class="fp-item-meta">
            ${date ? `<span class="fp-item-time">${timeAgo(date)}</span>` : ''}
          </div>
          <div class="fp-item-edit" hidden>
            <textarea class="fp-edit-input fp-edit-textarea" maxlength="2000">${esc(r.text || '')}</textarea>
            <div class="fp-edit-actions">
              <button type="button" class="fp-btn-sm fp-btn-sm--primary" data-act="save">Save</button>
              <button type="button" class="fp-btn-sm" data-act="cancel">Cancel</button>
            </div>
          </div>
        </div>
        <div class="fp-item-stats">
          <span class="fp-item-badge fp-item-badge--like" title="Likes">👍 ${likes}</span>
          <span class="fp-item-badge fp-item-badge--dislike" title="Dislikes">👎 ${dislikes}</span>
          <div class="fp-item-actions">
            <button type="button" class="fp-icon-btn" data-act="edit" aria-label="Edit">✏️</button>
            <button type="button" class="fp-icon-btn fp-icon-btn--danger" data-act="delete" aria-label="Delete">🗑️</button>
          </div>
        </div>`;

      const editWrap = li.querySelector('.fp-item-edit');
      const previewEl = li.querySelector('.fp-item-preview');
      const textarea  = li.querySelector('.fp-edit-input');

      li.querySelector('[data-act="edit"]').addEventListener('click', () => {
        editWrap.hidden = false;
        previewEl.hidden = true;
        textarea.focus();
      });
      li.querySelector('[data-act="cancel"]').addEventListener('click', () => {
        editWrap.hidden = true;
        previewEl.hidden = false;
        textarea.value = r.text || '';
      });
      li.querySelector('[data-act="save"]').addEventListener('click', async () => {
        const ok = await saveReplyEdit(r.questionId, r.id, textarea.value);
        if (ok) {
          const newText = textarea.value.trim();
          previewEl.textContent = newText.slice(0,160) + (newText.length > 160 ? '…' : '');
          editWrap.hidden = true;
          previewEl.hidden = false;
        }
      });
      li.querySelector('[data-act="delete"]').addEventListener('click', () => deleteReply(r.questionId, r.id));

      aList.appendChild(li);
    });
  }

  async function loadAll() {
    showError(false);
    try {
      myQuestions = await fetchMyQuestions(db, collectionName, customer.id);
      myReplies   = await fetchMyReplies(db, collectionName, customer.id, myQuestions);
      recomputeStats();
      renderQuestions();
      renderReplies();
    } catch (err) {
      console.error('Portal: failed to load activity', err);
      showError(true);
      [qList, aList].forEach(el => { if (el) el.innerHTML = ''; });
    }
  }

  qSort && qSort.addEventListener('change', renderQuestions);
  aSort && aSort.addEventListener('change', renderReplies);
  retryBtn && retryBtn.addEventListener('click', loadAll);

  document.querySelectorAll(`#fp-stats-bar-${sectionId} [data-stat-target]`).forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.statTarget;
      if (target === 'questions' || target === 'likes' || target === 'dislikes') {
        if (target !== 'questions' && qSort) qSort.value = 'liked';
        renderQuestions();
        qPanel && qPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (target === 'answers') {
        renderReplies();
        aPanel && aPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  await loadAll();
});
